import React, { useState } from 'react';
import { Box, Container, Paper, Tab, Tabs, Typography, Button, Alert, CircularProgress } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Psychology as AIIcon } from '@mui/icons-material';

import { dealsApi } from '../api';
import { baseClient } from '../api/baseClient';
import TaskList from '../components/TaskList';
import DocumentsPanel from '../components/DocumentsPanel';
import TeamChat from '../components/TeamChat';
import IntelligentEmailActivity from '../components/IntelligentEmailActivity';
import DealStageSelector from '../components/DealStageSelector';

// Type imports
import type { Deal, Task, Activity, ChatMessage } from '../types/api';

interface DealRoomData {
  deal: Deal;
  tasks: Task[];
  activities: Activity[];
  messages: ChatMessage[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function DealRoomPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const [value, setValue] = useState(0);
  const queryClient = useQueryClient();

  // AI Analysis mutation
  const aiAnalysisMutation = useMutation({
    mutationFn: async (dealId: string) => {
      return baseClient.post(`/deals/${dealId}/room/analyze`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dealRoom', dealId]);
    }
  });

  const { data, isLoading, error } = useQuery<DealRoomData>(
    ['dealRoom', dealId],
    async () => {
      if (!dealId) throw new Error('Deal ID is required');
      return await dealsApi.getDealRoom(dealId);
    },
    {
      enabled: !!dealId
    }
  );

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error || !data) {
    return <div>Error loading deal room</div>;
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h4">{data.deal.name}</Typography>
              <Typography variant="h6" color="text.secondary">${data.deal.value}</Typography>
              <Typography variant="body1"><strong>Stage:</strong> {data.deal.stage}</Typography>
              <Typography variant="body1"><strong>Created:</strong> {new Date(data.deal.createdAt).toLocaleDateString()}</Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AIIcon />}
              onClick={() => dealId && aiAnalysisMutation.mutate(dealId)}
              disabled={aiAnalysisMutation.isLoading}
              color="primary"
            >
              {aiAnalysisMutation.isLoading ? <CircularProgress size={20} /> : 'Run AI Analysis'}
            </Button>
          </Box>

          {aiAnalysisMutation.isSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              AI analysis completed! Check the insights in the tabs below.
            </Alert>
          )}

          {aiAnalysisMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to run AI analysis. Please try again.
            </Alert>
          )}

          <DealStageSelector deal={data.deal} />
        </Paper>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleTabChange} aria-label="deal room tabs">
            <Tab label="Tasks" {...a11yProps(0)} />
            <Tab label="Documents" {...a11yProps(1)} />
            <Tab label="Team Chat" {...a11yProps(2)} />
            <Tab label="Email Activity" {...a11yProps(3)} />
          </Tabs>
        </Box>

        <TabPanel value={value} index={0}>
          <TaskList tasks={data.tasks} dealId={data.deal.id} />
        </TabPanel>

        <TabPanel value={value} index={1}>
          <DocumentsPanel dealId={data.deal.id} />
        </TabPanel>

        <TabPanel value={value} index={2}>
          <TeamChat dealId={data.deal.id} />
        </TabPanel>

        <TabPanel value={value} index={3}>
          <IntelligentEmailActivity activity={data.activities[0]} />
        </TabPanel>
      </Box>
    </Container>
  );
} 